import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, doc, getDoc, getDocs } from 'firebase/firestore';
import { db } from '../config/firebase';
import { Phone, Clock, Mail, Send, Loader, X, Star, CheckCircle } from 'lucide-react';
import { FaWhatsapp, FaWaze } from 'react-icons/fa';
import { createPortal } from 'react-dom';

const Modal = ({ isOpen, onClose, children }) => {
  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div
        className="fixed inset-0 bg-black/60 transition-opacity backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-50 bg-gradient-to-br from-gray-900 to-gray-950 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-md sm:w-full mx-auto border border-amber-400/30 animate-slide-up">
        {children}
      </div>
    </div>,
    document.body
  );
};

const SuccessAnimation = () => (
  <div className="flex flex-col items-center justify-center p-8">
    <div className="w-20 h-20 bg-amber-500/15 rounded-full flex items-center justify-center border border-amber-400/30">
      <CheckCircle className="w-10 h-10 text-amber-400" />
    </div>
    <h3 className="text-2xl font-bold text-amber-100 mt-4 mb-2">הפנייה נשלחה!</h3>
    <p className="text-gray-400 text-center">נחזור אליכם בהקדם האפשרי</p>
  </div>
);

const inputClass =
  'w-full px-3 py-2.5 md:px-4 md:py-3 bg-white/5 border border-amber-400/25 rounded-lg md:rounded-xl focus:ring-1 focus:ring-amber-400/40 focus:border-amber-400/50 outline-none transition-all duration-200 text-white placeholder-gray-400 text-sm md:text-base';

const ContactPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [business, setBusiness] = useState(null);
  const [subjects, setSubjects] = useState([]);
  const [showHours, setShowHours] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [phoneError, setPhoneError] = useState('');
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    subject: '',
    debtAmount: '',
    termsAccepted: false,
  });

  const debtAmountOptions = [
    { value: '0-50000', label: '0 - 50,000 ₪' },
    { value: '50000-170000', label: '50,000 - 170,000 ₪' },
    { value: '170000+', label: 'מעל 170,000 ₪' },
  ];

  const isExecutionSubject = () => {
    const selectedSubject = subjects.find((s) => s.id === formData.subject);
    return (
      selectedSubject &&
      (selectedSubject.title === 'חדלות פרעון והוצאה לפועל' ||
        selectedSubject.title.includes('חדלות פירעון'))
    );
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
      ...(name === 'subject' &&
      !subjects.find((s) => s.id === value)?.title.includes('חדלות פירעון')
        ? { debtAmount: '' }
        : {}),
    }));
    if (name === 'phone') {
      setPhoneError('');
    }
  };

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });

    const style = document.createElement('style');
    style.textContent = `
      @keyframes slide-up {
        from { transform: translateY(100%); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      .animate-slide-up { animation: slide-up 0.3s ease-out; }
    `;
    document.head.appendChild(style);

    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const businessDoc = await getDoc(doc(db, 'businessDetails', 'kioGnL9sTArCVO1W54OL'));
      setBusiness(businessDoc.data());

      const subjectsSnapshot = await getDocs(collection(db, 'subjects'));
      const subjectsData = subjectsSnapshot.docs.map((docItem) => ({
        id: docItem.id,
        title: docItem.data().name,
      }));

      setSubjects(subjectsData);
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cleanedPhone = formData.phone.replace(/[^0-9]/g, '');
    if (cleanedPhone.length !== 10) {
      setPhoneError('מספר הטלפון חייב להכיל 10 ספרות בדיוק.');
      return;
    }
    setPhoneError('');

    setLoading(true);

    try {
      await addDoc(collection(db, 'leads'), {
        ...formData,
        phone: cleanedPhone,
        type: 'contact_form',
        createdAt: new Date().toISOString(),
      });

      setShowSuccess(true);
      setTimeout(() => {
        navigate('/thank-you');
      }, 2000);
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('אירעה שגיאה בשליחת הטופס. אנא נסו שוב.');
    } finally {
      setLoading(false);
    }
  };

  const quickLinks = [
    {
      href: business ? `tel:${business.number}` : '#',
      label: 'התקשר',
      icon: Phone,
      external: false,
      iconBg: 'bg-gradient-to-r from-green-400 to-green-500',
    },
    {
      href: 'https://wa.me/972559670867',
      label: 'וואטסאפ',
      icon: FaWhatsapp,
      external: true,
      iconBg: 'bg-gradient-to-r from-green-400 to-green-600',
    },
    {
      href: 'mailto:Chen@michelson-law.com',
      label: 'מייל',
      icon: Mail,
      external: false,
      iconBg: 'bg-gradient-to-r from-amber-500 to-yellow-500',
    },
    {
      href: 'https://waze.com/ul?ll=31.794418,34.6410578&navigate=yes',
      label: 'ניווט',
      icon: FaWaze,
      external: true,
      iconBg: 'bg-gradient-to-r from-purple-400 to-purple-500',
    },
    {
      label: 'שעות',
      icon: Clock,
      onClick: () => setShowHours(true),
      iconBg: 'bg-gradient-to-r from-orange-400 to-orange-500',
    },
  ];

  if (!business) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-gradient-to-br from-gray-950 via-gray-900 to-black">
        <div className="flex flex-col items-center gap-4">
          <Loader className="w-8 h-8 animate-spin text-amber-500" />
          <p className="text-gray-400">טוען...</p>
        </div>
      </div>
    );
  }

  const quickLinkClass =
    'group flex flex-1 min-w-0 flex-col items-center justify-center gap-1 md:gap-2 px-1 py-2 sm:px-2 sm:py-2.5 md:px-4 md:py-3 rounded-lg md:rounded-2xl bg-white/5 backdrop-blur-sm border border-amber-400/20 hover:border-amber-400/50 hover:bg-white/10 transition-all duration-300';

  const quickLinkIconWrapBase =
    'w-7 h-7 sm:w-8 sm:h-8 md:w-11 md:h-11 rounded-md sm:rounded-lg md:rounded-xl flex items-center justify-center group-hover:scale-105 transition-transform flex-shrink-0';

  const quickLinkIconSize = 'w-3.5 h-3.5 sm:w-4 sm:h-4 md:w-5 md:h-5 text-white';

  const quickLinkLabel =
    'text-amber-100/90 text-[9px] sm:text-[10px] md:text-sm font-medium whitespace-nowrap leading-none';

  return (
    <div
      className="relative min-h-[calc(100vh-5rem)] overflow-hidden bg-gradient-to-br from-gray-950 via-gray-900 to-black"
      dir="rtl"
    >
      <div className="pointer-events-none absolute -top-24 -right-24 h-72 w-72 rounded-full bg-amber-500/10 blur-3xl" />
      <div className="pointer-events-none absolute bottom-0 left-0 h-80 w-80 rounded-full bg-amber-600/5 blur-3xl" />
      <div className="pointer-events-none absolute top-1/2 left-1/2 h-px w-full max-w-3xl -translate-x-1/2 bg-gradient-to-r from-transparent via-amber-400/20 to-transparent" />

      <div className="relative z-10 max-w-3xl md:max-w-4xl mx-auto px-3 sm:px-4 md:px-6 pt-20 pb-6 md:pb-10">
        <div className="text-center mb-4 md:mb-7">
          <p className="text-amber-400/90 text-xs md:text-sm font-medium tracking-wide mb-2">מיכלסון ושות׳</p>
          <h1 className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-amber-200 via-yellow-300 to-amber-200 bg-clip-text text-transparent mb-2 md:mb-3">
            צור קשר
          </h1>
          <div className="w-14 md:w-20 h-0.5 md:h-1 bg-gradient-to-r from-amber-500 to-yellow-500 mx-auto rounded-full mb-2 md:mb-3" />
          <p className="text-gray-400 text-xs sm:text-sm md:text-base max-w-md mx-auto">
            ייעוץ משפטי מקצועי — השאירו פרטים ונחזור אליכם בהקדם
          </p>
        </div>

        <div className="rounded-2xl md:rounded-3xl border border-amber-400/15 bg-white/[0.03] backdrop-blur-md p-2 sm:p-3 md:p-5 mb-4 md:mb-6 shadow-xl shadow-black/20">
          <div className="flex flex-nowrap justify-between gap-1 sm:gap-2 md:gap-3">
            {quickLinks.map((link) => {
              const Icon = link.icon;
              const inner = (
                <>
                  <div className={`${quickLinkIconWrapBase} ${link.iconBg}`}>
                    <Icon className={quickLinkIconSize} />
                  </div>
                  <span className={quickLinkLabel}>{link.label}</span>
                </>
              );

              if (link.onClick) {
                return (
                  <button key={link.label} type="button" onClick={link.onClick} className={quickLinkClass}>
                    {inner}
                  </button>
                );
              }

              return (
                <a
                  key={link.label}
                  href={link.href}
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                  className={quickLinkClass}
                >
                  {inner}
                </a>
              );
            })}
          </div>
        </div>

        <div className="relative rounded-2xl md:rounded-3xl p-[1px] bg-gradient-to-br from-amber-400/40 via-amber-500/20 to-transparent shadow-2xl shadow-amber-950/30">
          <div className="bg-gradient-to-br from-gray-900 via-gray-800 to-black rounded-2xl md:rounded-3xl p-4 sm:p-5 md:p-7 lg:p-8">
            <h2 className="flex items-center justify-center gap-2 text-lg md:text-xl font-bold text-amber-100 mb-4 md:mb-5">
              <Send className="w-[1em] h-[1em] text-amber-400 flex-shrink-0" />
              <span>השאירו פרטים</span>
            </h2>

            <form onSubmit={handleSubmit} className="space-y-3 md:space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4">
              <div>
                <label className="block text-xs md:text-sm font-medium text-amber-100/80 mb-1 md:mb-1.5">שם מלא</label>
                <input
                  type="text"
                  name="fullName"
                  required
                  value={formData.fullName}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="שם מלא"
                />
              </div>
              <div>
                <label className="block text-xs md:text-sm font-medium text-amber-100/80 mb-1 md:mb-1.5">טלפון</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={inputClass}
                  placeholder="מספר טלפון"
                  dir="rtl"
                />
                {phoneError && <p className="text-red-300 text-xs mt-1 text-right">{phoneError}</p>}
              </div>
            </div>

            <div>
              <label className="block text-xs md:text-sm font-medium text-amber-100/80 mb-1 md:mb-1.5">נושא הפנייה</label>
              <select
                name="subject"
                required
                value={formData.subject}
                onChange={handleInputChange}
                className={`${inputClass} appearance-none`}
              >
                <option value="" className="bg-gray-800 text-white">
                  בחר נושא
                </option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id} className="bg-gray-800 text-white">
                    {subject.title}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-start gap-2 md:gap-3">
              <input
                type="checkbox"
                name="termsAccepted"
                id="terms-contact"
                required
                checked={formData.termsAccepted}
                onChange={handleInputChange}
                className="mt-0.5 w-3.5 h-3.5 md:w-4 md:h-4 text-amber-500 bg-white/5 border-amber-400/40 rounded focus:ring-amber-400/50 flex-shrink-0"
              />
              <label htmlFor="terms-contact" className="text-[11px] sm:text-xs md:text-sm text-gray-400 cursor-pointer leading-snug">
                אני מאשר/ת את{' '}
                <a href="/terms" target="_blank" className="text-amber-400 underline hover:text-amber-300">
                  תנאי השימוש
                </a>{' '}
                ו
                <a href="/privacy" target="_blank" className="text-amber-400 underline hover:text-amber-300">
                  מדיניות הפרטיות
                </a>{' '}
                וקבלת פניות מהמשרד
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !formData.termsAccepted}
              className={`
                w-full py-2.5 sm:py-3 md:py-3.5 rounded-lg md:rounded-xl text-sm sm:text-base md:text-lg font-bold
                flex items-center justify-center gap-2
                ${
                  loading || !formData.termsAccepted
                    ? 'bg-gray-600/50 cursor-not-allowed opacity-50'
                    : 'bg-gradient-to-r from-amber-600 to-yellow-600 hover:from-amber-500 hover:to-yellow-500'
                }
                text-white transition-all duration-200 border border-amber-400/30
              `}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  <span>שולח...</span>
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 md:w-5 md:h-5" />
                  <span>שלח פנייה</span>
                </>
              )}
            </button>

            <div className="flex flex-wrap justify-center items-center gap-3 sm:gap-5 md:gap-6 text-gray-400 text-[10px] sm:text-xs md:text-sm pt-1 md:pt-2">
              <div className="flex items-center gap-1">
                <CheckCircle className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-500" />
                <span>מאובטח</span>
              </div>
              <div className="flex items-center gap-1">
                <Star className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-500" />
                <span>ללא חיוב</span>
              </div>
              <div className="flex items-center gap-1">
                <Clock className="w-3 h-3 md:w-3.5 md:h-3.5 text-amber-500" />
                <span>מענה מהיר</span>
              </div>
            </div>
            </form>
          </div>
        </div>
      </div>

      <Modal isOpen={showHours} onClose={() => setShowHours(false)}>
        <div className="p-6 sm:p-8" dir="rtl">
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-11 h-11 bg-gradient-to-r from-orange-400 to-orange-500 rounded-xl flex items-center justify-center">
                <Clock className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-amber-100">שעות פעילות</h3>
            </div>
            <button
              type="button"
              onClick={() => setShowHours(false)}
              className="p-2 hover:bg-white/10 rounded-full transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          <div className="space-y-3">
            <div className="flex justify-between items-center py-3.5 px-4 bg-white/5 rounded-xl border border-amber-400/20">
              <span className="font-semibold text-gray-200">ראשון - חמישי</span>
              <span className="text-amber-400 font-bold">09:00 - 18:00</span>
            </div>
            <div className="flex justify-between items-center py-3.5 px-4 bg-white/5 rounded-xl border border-white/10">
              <span className="font-semibold text-gray-200">שישי</span>
              <span className="text-gray-500 font-medium">סגור</span>
            </div>
            <div className="flex justify-between items-center py-3.5 px-4 bg-white/5 rounded-xl border border-white/10">
              <span className="font-semibold text-gray-200">שבת</span>
              <span className="text-gray-500 font-medium">סגור</span>
            </div>
          </div>

          <div className="mt-6 p-4 bg-amber-500/10 rounded-xl border border-amber-400/25">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Phone className="w-4 h-4 text-white" />
              </div>
              <div>
                <p className="text-sm text-amber-100/90 font-medium">
                  <strong>שירות 24/7:</strong> במקרים דחופים ניתן להשאיר הודעה בטלפון
                </p>
                <p className="text-xs text-gray-400 mt-1">נחזור אליכם בהקדם האפשרי</p>
              </div>
            </div>
          </div>
        </div>
      </Modal>

      <Modal isOpen={showSuccess} onClose={() => setShowSuccess(false)}>
        <SuccessAnimation />
      </Modal>
    </div>
  );
};

export default ContactPage;
